import type React from 'react'
import {
  LuBookCopy,
  LuCalendar,
  LuCalendarCheck,
  LuCalendarOff,
  LuChartLine,
  LuChartPie,
  LuClipboard,
  LuClipboardList,
  LuFileText,
  LuHouse,
  LuInbox,
  LuListChecks,
  LuMap,
  LuPackage,
  LuPlane,
  LuReceipt,
  LuSettings,
  LuShip,
  LuSignature,
  LuUsers,
  LuZap,
} from 'react-icons/lu'
import type { Role } from '../../hooks/useAuth'

export type SidebarChild = {
  key: string
  label: string
  to: string
  icon?: React.ComponentType<{ size?: number }>
  roles?: Role[]
}

export type SidebarItem = {
  key: string
  label: string
  to?: string
  icon: React.ComponentType<{ size?: number }>
  roles?: Role[]
  section?: string
  children?: SidebarChild[]
}

/**
 * Pelabuhan sidebar — Indonesian maritime terminology.
 *
 * Translation philosophy:
 *  - Top-level + sub-pages: Indonesian for warm, locally-grounded identity.
 *  - Loanwords kept where common in Indonesian SaaS usage (e.g. "Booking").
 *  - "Dermaga" (berth/quay) used where the maritime metaphor is exact;
 *    "Pelaut" avoided as it implies sailor profession (Pelanggan = customer).
 */
export const sidebarItems: SidebarItem[] = [
  {
    key: 'overview',
    // "Ikhtisar" = Overview/Summary — lebih tepat untuk ops dashboard vs "Beranda" yang berkesan halaman website
    label: 'Ikhtisar',
    to: '/dashboard',
    icon: LuHouse,
  },
  {
    key: 'analytics',
    label: 'Analitik',
    to: '/dashboard/analytics',
    icon: LuChartLine,
  },
  {
    key: 'occupancy',
    // "Dermaga" = berth/dock area — istilah maritim langsung, menggantikan loanword "Okupansi" yang terasa hotel/RS
    label: 'Dermaga',
    icon: LuChartPie,
    section: 'Operasional',
    children: [
      { key: 'map', label: 'Peta', to: '/dashboard/occupancy/map', icon: LuMap },
      { key: 'schedule', label: 'Jadwal', to: '/dashboard/occupancy/calendar', icon: LuCalendar },
      { key: 'absences', label: 'Absen', to: '/dashboard/occupancy/absences', icon: LuCalendarOff },
      { key: 'arrivals', label: 'Tiba & Berangkat', to: '/dashboard/occupancy/arrivals', icon: LuPlane },
    ],
  },
  {
    key: 'reservations',
    // "Tambat" = mooring/berthing — kata kerja dalam konteks marina (berlabuh = to moor);
    // menggantikan "Reservasi" yang generik dengan istilah marina yang lebih khas
    label: 'Tambat',
    icon: LuClipboardList,
    children: [
      { key: 'requests', label: 'Permohonan', to: '/dashboard/reservations/requests', icon: LuInbox },
      { key: 'bookings', label: 'Booking', to: '/dashboard/reservations/bookings', icon: LuCalendarCheck },
    ],
  },
  {
    key: 'billing',
    // "Penagihan" = Invoicing (proses) vs "Tagihan" (hasil/bill) — lebih aktif dan presisi
    label: 'Penagihan',
    icon: LuReceipt,
    roles: ['MANAGER', 'SUPER_ADMIN'],
    section: 'Keuangan',
    children: [
      { key: 'quotes', label: 'Penawaran', to: '/dashboard/billing/quotes', icon: LuFileText },
      { key: 'invoices', label: 'Faktur', to: '/dashboard/billing/invoices', icon: LuBookCopy },
    ],
  },
  {
    key: 'accounts',
    // "Direktori" = Directory — istilah SaaS modern yang tepat untuk registry pelanggan, kapal & kontrak;
    // menggantikan "Akun" yang di Indonesia kini identik dengan akun media sosial
    label: 'Direktori',
    icon: LuUsers,
    section: 'Manajemen',
    children: [
      { key: 'customers', label: 'Pelanggan', to: '/dashboard/accounts/customers', icon: LuUsers },
      { key: 'boats', label: 'Kapal', to: '/dashboard/accounts/boats', icon: LuShip },
      {
        key: 'contracts',
        label: 'Kontrak',
        to: '/dashboard/accounts/contracts',
        icon: LuSignature,
        roles: ['MANAGER', 'SUPER_ADMIN'],
      },
    ],
  },
  {
    key: 'facility',
    // "Logistik" mencakup tugas staf (assignments) + inventaris barang — lebih presisi dari "Fasilitas"
    label: 'Logistik',
    icon: LuClipboard,
    children: [
      { key: 'tasks', label: 'Tugas', to: '/dashboard/facility/assignments', icon: LuListChecks },
      { key: 'inventory', label: 'Inventaris', to: '/dashboard/facility/inventory', icon: LuPackage },
    ],
  },
  {
    key: 'utilities',
    label: 'Utilitas',
    to: '/dashboard/utilities',
    icon: LuZap,
    section: 'Sistem',
  },
  {
    key: 'settings',
    label: 'Pengaturan',
    to: '/dashboard/settings',
    icon: LuSettings,
    roles: ['SUPER_ADMIN', 'MANAGER'],
  },
]

export function filterSidebarByRole(items: SidebarItem[], role: Role | undefined): SidebarItem[] {
  if (!role) return []
  return items
    .filter((item) => !item.roles || item.roles.includes(role))
    .map((item) => ({
      ...item,
      children: item.children?.filter((c) => !c.roles || c.roles.includes(role)),
    }))
}
